////
/// ============================================================================
/// COMPONENT / Draggable Gallery
/// ============================================================================
///
/// @link https://codyhouse.co/ds/components/app/draggable-image-gallery
/// @group components
////

import SwipeContent from "../utils/swipe-content";

export default () => {
    var DragGallery = function (element) {
        this.element = element;
        this.list = this.element.getElementsByTagName('ul')[0];
        this.imgs = this.list.children;
        this.gestureHint = this.element.getElementsByClassName('c-drag-gallery_gesture-hint');// drag gesture hint
        this.galleryWidth = getGalleryWidth(this);
        this.translate = 0; // store container translate value
        this.dragStart = false; // start dragging position
        // drag momentum option
        this.dragMStart = false;
        this.dragTimeMStart = false;
        this.dragTimeMEnd = false;
        this.dragMSpeed = false;
        this.dragAnimId = false;
        initDragGalleryEvents(this);
    };

    // Check if Reduced Motion is enabled
    function osHasReducedMotion() {
        if (!window.matchMedia) {
            return false;
        }
        var matchMediaObj = window.matchMedia('(prefers-reduced-motion: reduce)');
        if (matchMediaObj) {
            return matchMediaObj.matches;
        }
        return false; // return false if not supported
    }

    function initDragGalleryEvents(gallery) {
        initDragging(gallery); // init dragging

        gallery.element.addEventListener('update-gallery-width', function (event) { // window resize
            gallery.galleryWidth = getGalleryWidth(gallery);
            // reset translate value if not acceptable
            checkTranslateValue(gallery);
            setTranslate(gallery);
        });

        if (intersectionObsSupported) {
            initOpacityAnim(gallery);
        } // init image animation

        if (!reducedMotion && gallery.gestureHint.length > 0) {
            initHintGesture(gallery);
        } // init hint gesture element animation

        initKeyBoardNav(gallery);
    }

    function getGalleryWidth(gallery) {
        return gallery.list.scrollWidth - gallery.list.offsetWidth;
    }

    function initDragging(gallery) { // gallery drag
        new SwipeContent(gallery.element);
        gallery.element.addEventListener('dragStart', function (event) {
            window.cancelAnimationFrame(gallery.dragAnimId);
            gallery.element.classList.add('is-dragging');
            gallery.dragStart = event.detail.x;
            gallery.dragMStart = event.detail.x;
            gallery.dragTimeMStart = new Date().getTime();
            gallery.dragTimeMEnd = false;
            gallery.dragMSpeed = false;
            initDragEnd(gallery);
        });

        gallery.element.addEventListener('dragging', function (event) {
            if (!gallery.dragStart) return;
            if (Math.abs(event.detail.x - gallery.dragStart) < 5) return;
            gallery.translate = Math.round(event.detail.x - gallery.dragStart + gallery.translate);
            gallery.dragStart = event.detail.x;
            checkTranslateValue(gallery);
            setTranslate(gallery);
        });
    }

    function initDragEnd(gallery) {
        gallery.element.addEventListener('dragEnd', function cb(event) {
            gallery.element.removeEventListener('dragEnd', cb);
            gallery.element.classList.remove('is-dragging');
            initMomentumDrag(gallery); // drag momentum
            gallery.dragStart = false;
        });
    }

    function initKeyBoardNav(gallery) {
        gallery.element.setAttribute('tabindex', 0);
        // navigate gallery using right/left arrows
        gallery.element.addEventListener('keyup', function (event) {
            if (event.keyCode && event.keyCode === 39 || event.key && event.key.toLowerCase() === 'arrowright') {
                keyboardNav(gallery, 'right');
            } else if (event.keyCode && event.keyCode === 37 || event.key && event.key.toLowerCase() === 'arrowleft') {
                keyboardNav(gallery, 'left');
            }
        });
    }

    function keyboardNav(gallery, direction) {
        var delta = parseFloat(window.getComputedStyle(gallery.imgs[0]).marginRight) + gallery.imgs[0].offsetWidth;
        gallery.translate = (direction === 'right') ? gallery.translate - delta : gallery.translate + delta;
        checkTranslateValue(gallery);
        setTranslate(gallery);
    }

    function checkTranslateValue(gallery) { // make sure translate is in the right interval
        if (gallery.translate > 0) {
            gallery.translate = 0;
            gallery.dragMSpeed = 0;
        }
        if (Math.abs(gallery.translate) > gallery.galleryWidth) {
            gallery.translate = gallery.galleryWidth * -1;
            gallery.dragMSpeed = 0;
        }
    }

    function setTranslate(gallery) {
        gallery.list.style.transform = 'translateX(' + gallery.translate + 'px)';
        gallery.list.style.msTransform = 'translateX(' + gallery.translate + 'px)';
    };

    function initOpacityAnim(gallery) { // animate img opacities on drag
        for (var i = 0; i < gallery.imgs.length; i++) {
            var observer = new IntersectionObserver(opacityCallback.bind(gallery.imgs[i]), {threshold: [0, 0.1]});
            observer.observe(gallery.imgs[i]);
        }
    }

    function opacityCallback(entries, observer) { // reveal images when they enter the viewport
        var threshold = entries[0].intersectionRatio.toFixed(1);
        if (threshold > 0) {
            this.classList.add('is-visible');
            observer.unobserve(this);
        }
    }

    function initMomentumDrag(gallery) {
        // momentum effect when drag is over
        if (reducedMotion) return;
        var timeNow = new Date().getTime();
        gallery.dragMSpeed = 0.95 * (gallery.dragStart - gallery.dragMStart) / (timeNow - gallery.dragTimeMStart);

        var currentTime = false;

        function animMomentumDrag(timestamp) {
            if (!currentTime) currentTime = timestamp;
            var progress = timestamp - currentTime;
            currentTime = timestamp;
            if (Math.abs(gallery.dragMSpeed) < 0.01) {
                gallery.dragAnimId = false;
                return;
            } else {
                gallery.translate = Math.round(gallery.translate + (gallery.dragMSpeed * progress));
                checkTranslateValue(gallery);
                setTranslate(gallery);
                gallery.dragMSpeed = gallery.dragMSpeed * 0.95;
                gallery.dragAnimId = window.requestAnimationFrame(animMomentumDrag);
            }
        }

        gallery.dragAnimId = window.requestAnimationFrame(animMomentumDrag);
    }

    function initHintGesture(gallery) { // show user a hint about gallery dragging
        var observer = new IntersectionObserver(hintGestureCallback.bind(gallery.gestureHint[0]), {threshold: [0, 1]});
        observer.observe(gallery.gestureHint[0]);
    }

    function hintGestureCallback(entries, observer) {
        var threshold = entries[0].intersectionRatio.toFixed(1);
        if (threshold > 0) {
            this.classList.add('is-animate');
            observer.unobserve(this);
        }
    }

    // initialize the DragGallery objects
    var dragGallery = document.getElementsByClassName('js-drag-gallery'),
        intersectionObsSupported = ('IntersectionObserver' in window && 'IntersectionObserverEntry' in window && 'intersectionRatio' in window.IntersectionObserverEntry.prototype),
        reducedMotion = osHasReducedMotion();

    if (dragGallery.length > 0) {
        var dragGalleryArray = [];
        for (var i = 0; i < dragGallery.length; i++) {
            (function (i) {
                if (!intersectionObsSupported || reducedMotion) {
                    dragGallery[i].classList.add('is-anim-off');
                }
                dragGalleryArray.push(new DragGallery(dragGallery[i]));
            })(i);
        }

        // resize event
        var resizingId = false,
            customEvent = new CustomEvent('update-gallery-width');

        window.addEventListener('resize', function () {
            clearTimeout(resizingId);
            resizingId = setTimeout(doneResizing, 500);
        });

        function doneResizing() {
            for (var i = 0; i < dragGalleryArray.length; i++) {
                (function (i) {
                    dragGalleryArray[i].element.dispatchEvent(customEvent)
                })(i);
            }
        }
    }
};